import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import type { Genre } from '@/types'

const GENRES_QUERY_KEY = 'genres'

interface UseGenresOptions {
  preferredFirst?: boolean
  preferredGenreIds?: string[]
  preferredOnly?: boolean
}

// Get all genres with translations (public data)
export function useGenres(options?: UseGenresOptions) {
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language

  const baseQuery = useQuery({
    queryKey: [GENRES_QUERY_KEY, currentLanguage],
    queryFn: async (): Promise<Genre[]> => {
      const { data, error } = await supabase
        .from('genres')
        .select('*')
        .order('name', { ascending: true })

      if (error) throw error

      // Map data to include translated names
      return (data || []).map((genre) => ({
        ...genre,
        // Use translated name based on current language, fallback to English
        name: getTranslatedGenreName(genre, currentLanguage) || genre.name,
      }))
    },
  })

  // Apply filtering and sorting based on options
  const processedData = useMemo(() => {
    if (!baseQuery.data || !options) return baseQuery.data

    const { preferredFirst, preferredGenreIds, preferredOnly } = options

    let genres = [...baseQuery.data]

    // Filter to preferred only if requested
    if (preferredOnly && preferredGenreIds?.length) {
      genres = genres.filter((genre) => preferredGenreIds.includes(genre.id))
    }

    // Sort preferred first if requested
    if (preferredFirst && preferredGenreIds?.length) {
      genres.sort((a, b) => {
        const aIsPreferred = preferredGenreIds.includes(a.id)
        const bIsPreferred = preferredGenreIds.includes(b.id)

        // Both preferred or both not preferred - sort by name
        if (aIsPreferred === bIsPreferred) {
          return a.name.localeCompare(b.name)
        }

        // Preferred genres come first
        return aIsPreferred ? -1 : 1
      })
    }

    return genres
  }, [baseQuery.data, options])

  return {
    ...baseQuery,
    data: processedData,
  }
}

// Helper function to get translated genre name
function getTranslatedGenreName(
  genre: {
    name_pt?: string | null
    name_en?: string | null
    name_es?: string | null
    name: string
  },
  language: string
): string {
  switch (language) {
    case 'pt':
    case 'pt-BR':
      return genre.name_pt || genre.name_en || genre.name
    case 'es':
    case 'es-ES':
      return genre.name_es || genre.name_en || genre.name
    default:
      return genre.name_en || genre.name
  }
}

// Type for the join result from Supabase
interface BookGenreJoinResult {
  genres: Genre | null
}

// Get genres for a specific book
export function useBookGenres(bookId: string) {
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language

  return useQuery({
    queryKey: [GENRES_QUERY_KEY, 'book', bookId, currentLanguage],
    queryFn: async (): Promise<Genre[]> => {
      if (!bookId) return []

      const { data, error } = await supabase
        .from('book_genres')
        .select(`
          genres (
            id,
            name,
            slug,
            name_pt,
            name_en,
            name_es,
            color,
            created_at
          )
        `)
        .eq('book_id', bookId)

      if (error) throw error

      // Handle the join result structure and apply translations
      return (
        data?.flatMap((item) => {
          const typedItem = item as unknown as BookGenreJoinResult
          if (!typedItem.genres) return []

          return [
            {
              ...typedItem.genres,
              name:
                getTranslatedGenreName(typedItem.genres, currentLanguage) || typedItem.genres.name,
            },
          ]
        }) || []
      )
    },
    enabled: !!bookId,
  })
}

// Update book genres (replaces all existing genres for a book)
export function useUpdateBookGenres() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ bookId, genreIds }: { bookId: string; genreIds: string[] }) => {
      // First, delete existing genres for this book
      const { error: deleteError } = await supabase
        .from('book_genres')
        .delete()
        .eq('book_id', bookId)

      if (deleteError) throw deleteError

      // Then, insert new genres
      if (genreIds.length > 0) {
        const bookGenres = genreIds.map((genreId) => ({
          book_id: bookId,
          genre_id: genreId,
        }))

        const { error: insertError } = await supabase.from('book_genres').insert(bookGenres)

        if (insertError) throw insertError
      }
    },
    onSuccess: (_, { bookId }) => {
      queryClient.invalidateQueries({ queryKey: [GENRES_QUERY_KEY, 'book', bookId] })
    },
  })
}
